import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography
} from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import {
  fieldDisplayLabel,
  isInkFieldType,
  isMergeField,
  pdfRectToScreen
} from '../../utils/esignFields';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const FIELD_COLORS = {
  signature: '#1b5e20',
  initials: '#0d47a1',
  dateSigned: '#e65100',
  name: '#00695c',
  text: '#37474f',
  checkbox: '#455a64',
  merge: '#6a1b9a'
};

async function loadPdfBytes(url) {
  const res = await fetch(url, {
    method: 'GET',
    credentials: 'omit',
    cache: 'no-store'
  });
  if (!res.ok) {
    throw new Error(`Unexpected server response (${res.status}) while retrieving PDF`);
  }
  const buf = await res.arrayBuffer();
  if (!buf || buf.byteLength < 100) {
    throw new Error('PDF download was empty or truncated');
  }
  const head = new Uint8Array(buf.slice(0, 5));
  const magic = String.fromCharCode(...head);
  if (!magic.startsWith('%PDF')) {
    throw new Error('Downloaded file is not a PDF');
  }
  return buf;
}

function canvasToObjectUrl(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Could not encode page preview'));
          return;
        }
        resolve(URL.createObjectURL(blob));
      },
      'image/jpeg',
      0.82
    );
  });
}

function isCheckedValue(v) {
  return v === true || v === 'true' || v === 1 || v === '1' || v === 'yes';
}

function isMineField(field, highlightSigner) {
  if (!highlightSigner) return true;
  const e = String(highlightSigner.email || '')
    .trim()
    .toLowerCase();
  const r = String(highlightSigner.role || '')
    .trim()
    .toLowerCase();
  const fe = String(field.signerEmail || '')
    .trim()
    .toLowerCase();
  const fr = String(field.role || '')
    .trim()
    .toLowerCase();
  if (!fe && !fr) return true;
  if (fe && e && fe === e) return true;
  if (fr && r && fr === r) return true;
  return false;
}

function overlayInner(field, value, mergePreview) {
  if (field.type === 'checkbox') {
    return isCheckedValue(value) ? '✓' : '☐';
  }
  if (isInkFieldType(field.type) && typeof value === 'string' && value.startsWith('data:image')) {
    return (
      <Box
        component="img"
        src={value}
        alt=""
        sx={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
      />
    );
  }
  if (isMergeField(field)) {
    const mv =
      (mergePreview && (mergePreview[field.id] ?? mergePreview[field.mergeKey])) || '';
    return String(mv || '').trim() || fieldDisplayLabel(field);
  }
  if (value != null && String(value).trim() && !String(value).startsWith('data:')) {
    return String(value).trim();
  }
  return fieldDisplayLabel(field);
}

/**
 * Multi-page PDF preview with optional BYO field overlays.
 * Checkboxes can be activated on the document (tap → confirm dialog).
 */
const PdfScrollPreview = ({
  url,
  fallbackUrl = null,
  openUrl = null,
  openSignatureUrl = null,
  fields = null,
  fieldValues = null,
  mergePreview = null,
  highlightSigner = null,
  interactiveTypes = null,
  disabled = false,
  onFieldActivate = null,
  onFieldValueChange = null
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const [pages, setPages] = useState([]);
  const [activeUrl, setActiveUrl] = useState(url);
  const [checkboxPrompt, setCheckboxPrompt] = useState(null);
  const [promptChecked, setPromptChecked] = useState(false);

  const overlayFields = Array.isArray(fields) ? fields : [];
  const interactiveSet = Array.isArray(interactiveTypes) ? new Set(interactiveTypes) : null;

  const canActivate = (field, mine) => {
    if (disabled || !mine || isMergeField(field)) return false;
    if (!interactiveSet) return Boolean(onFieldActivate || onFieldValueChange);
    return interactiveSet.has(field.type);
  };

  useEffect(() => {
    setActiveUrl(url);
  }, [url]);

  useEffect(() => {
    if (!activeUrl) return undefined;

    let cancelled = false;
    const objectUrls = [];

    const render = async () => {
      setLoading(true);
      setError('');
      setPages([]);
      setProgress('Downloading PDF…');

      try {
        const bytes = await loadPdfBytes(activeUrl);
        if (cancelled) return;

        setProgress(
          `Downloaded ${(bytes.byteLength / 1024 / 1024).toFixed(1)} MB — rendering…`
        );

        const task = pdfjsLib.getDocument({
          data: bytes,
          disableRange: true,
          disableStream: true,
          disableAutoFetch: true,
          isEvalSupported: false
        });
        const pdf = await task.promise;
        if (cancelled) return;

        const fitScale = 1.0;
        const nextPages = [];

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (cancelled) return;
          setProgress(`Rendering page ${pageNum} of ${pdf.numPages}…`);

          try {
            // eslint-disable-next-line no-await-in-loop
            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale: fitScale });
            const canvas = document.createElement('canvas');
            const maxPx = 1400;
            const pixelScale = Math.min(1, maxPx / Math.max(viewport.width, viewport.height));
            canvas.width = Math.max(1, Math.floor(viewport.width * pixelScale));
            canvas.height = Math.max(1, Math.floor(viewport.height * pixelScale));
            const ctx = canvas.getContext('2d', { alpha: false });
            if (pixelScale !== 1) ctx.setTransform(pixelScale, 0, 0, pixelScale, 0, 0);
            // eslint-disable-next-line no-await-in-loop
            await page.render({ canvasContext: ctx, viewport }).promise;
            // eslint-disable-next-line no-await-in-loop
            const src = await canvasToObjectUrl(canvas);
            objectUrls.push(src);
            nextPages.push({
              pageNum,
              pageIndex: pageNum - 1,
              src,
              total: pdf.numPages,
              pageWidth: viewport.width / fitScale,
              pageHeight: viewport.height / fitScale
            });
            setPages([...nextPages]);
          } catch (pageErr) {
            console.error(`❌ PDF page ${pageNum} render failed:`, pageErr);
            nextPages.push({
              pageNum,
              pageIndex: pageNum - 1,
              src: null,
              total: pdf.numPages,
              failed: true,
              pageWidth: 612,
              pageHeight: 792
            });
            setPages([...nextPages]);
          }
        }

        if (!cancelled) setProgress('');
      } catch (err) {
        console.error('❌ PDF preview render failed:', err);
        const msg = String(err?.message || err || '');
        if (!cancelled && fallbackUrl && activeUrl !== fallbackUrl) {
          console.warn('⚠️ Retrying PDF preview with fallback URL:', msg);
          setActiveUrl(fallbackUrl);
          return;
        }
        if (!cancelled) {
          setError(msg || 'Could not render PDF preview');
          setProgress('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    render();
    return () => {
      cancelled = true;
      objectUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [activeUrl, fallbackUrl]);

  const openField = (field) => {
    if (field.type === 'checkbox') {
      setPromptChecked(isCheckedValue(fieldValues?.[field.id]));
      setCheckboxPrompt(field);
      return;
    }
    if (onFieldActivate) onFieldActivate(field);
  };

  const confirmCheckbox = () => {
    if (!checkboxPrompt) return;
    if (onFieldValueChange) onFieldValueChange(checkboxPrompt.id, promptChecked);
    setCheckboxPrompt(null);
  };

  if (!activeUrl && !url) {
    return <Alert severity="info">No PDF preview URL available for this request.</Alert>;
  }

  const total = pages[0]?.total || 0;
  const hasInteractiveCheckboxes =
    overlayFields.some((f) => f.type === 'checkbox') &&
    (!interactiveSet || interactiveSet.has('checkbox'));

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        {openUrl && (
          <Button size="small" variant="outlined" href={openUrl} target="_blank" rel="noopener noreferrer">
            Open full PDF
          </Button>
        )}
        {openSignatureUrl && (
          <Button
            size="small"
            variant="text"
            href={openSignatureUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Jump to signature page
          </Button>
        )}
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            {progress || 'Loading document preview…'}
          </Typography>
        </Box>
      )}

      {!loading && total > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Showing all {total} pages below — keep scrolling to review the full document.
          {overlayFields.length > 0 && hasInteractiveCheckboxes
            ? ' Tap a checkbox box on the PDF to read and confirm that acknowledgment in place.'
            : overlayFields.length > 0
              ? ' Colored boxes show where your answers will stamp after you sign.'
              : ''}
        </Alert>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          In-app preview failed ({error}). Use <strong>Open full PDF</strong> to review all pages.
        </Alert>
      )}

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.5,
          bgcolor: 'grey.50'
        }}
      >
        {pages.map((p) => {
          const pageFields = overlayFields.filter(
            (f) => Number(f.pageIndex ?? f.page) === p.pageIndex
          );
          return (
            <Box key={p.pageNum} sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Page {p.pageNum} of {p.total}
                {pageFields.length > 0 ? ` · ${pageFields.length} field(s)` : ''}
              </Typography>
              {p.failed || !p.src ? (
                <Alert severity="error">Page {p.pageNum} could not be rendered in-app.</Alert>
              ) : (
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    bgcolor: 'common.white',
                    overflow: 'visible'
                  }}
                >
                  <Box
                    component="img"
                    src={p.src}
                    alt={`Document page ${p.pageNum} of ${p.total}`}
                    sx={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                  {pageFields.map((f) => {
                    const box = pdfRectToScreen(f, p.pageHeight, 1);
                    const mine = isMineField(f, highlightSigner);
                    const color = FIELD_COLORS[f.type] || FIELD_COLORS.text;
                    const value = fieldValues?.[f.id];
                    const filled =
                      f.type === 'checkbox'
                        ? isCheckedValue(value)
                        : isMergeField(f)
                          ? Boolean(
                              String(
                                mergePreview?.[f.id] ?? mergePreview?.[f.mergeKey] ?? ''
                              ).trim()
                            )
                          : Boolean(value);
                    const interactive = canActivate(f, mine);
                    const isCheckbox = f.type === 'checkbox';
                    return (
                      <Box
                        key={f.id}
                        role={interactive ? 'button' : undefined}
                        tabIndex={interactive ? 0 : undefined}
                        aria-label={
                          interactive
                            ? `${fieldDisplayLabel(f)}${filled ? ' (completed)' : ' (tap to complete)'}`
                            : fieldDisplayLabel(f)
                        }
                        title={
                          interactive
                            ? `Tap to ${isCheckbox ? 'confirm' : 'complete'}: ${fieldDisplayLabel(f)}`
                            : fieldDisplayLabel(f)
                        }
                        onClick={(e) => {
                          if (!interactive) return;
                          e.preventDefault();
                          e.stopPropagation();
                          openField(f);
                        }}
                        onKeyDown={(e) => {
                          if (!interactive) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openField(f);
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          left: `${(box.left / p.pageWidth) * 100}%`,
                          top: `${(box.top / p.pageHeight) * 100}%`,
                          width: `${(box.width / p.pageWidth) * 100}%`,
                          height: `${(box.height / p.pageHeight) * 100}%`,
                          minWidth: isCheckbox && interactive ? 28 : 14,
                          minHeight: isCheckbox && interactive ? 28 : 12,
                          border: `2px solid ${color}`,
                          bgcolor: filled ? `${color}55` : `${color}28`,
                          opacity: mine ? 1 : 0.4,
                          borderRadius: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden',
                          pointerEvents: interactive ? 'auto' : 'none',
                          cursor: interactive ? 'pointer' : 'default',
                          px: 0.25,
                          boxSizing: 'border-box',
                          boxShadow: interactive && !filled ? `0 0 0 2px ${color}88` : undefined,
                          WebkitTapHighlightColor: 'transparent',
                          '&:hover': interactive
                            ? { bgcolor: `${color}44`, zIndex: 2 }
                            : undefined
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            fontSize: isCheckbox ? 16 : 8,
                            lineHeight: 1.1,
                            fontWeight: 700,
                            color: '#111',
                            textAlign: 'center',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: isCheckbox ? 'nowrap' : 'normal',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            pointerEvents: 'none'
                          }}
                        >
                          {overlayInner(f, value, mergePreview)}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
        {!loading && !error && pages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No pages to show.
          </Typography>
        )}
        {!loading && pages.length > 0 && pages.length === total && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            End of document ({total} pages).
          </Typography>
        )}
      </Box>

      <Dialog
        open={Boolean(checkboxPrompt)}
        onClose={() => setCheckboxPrompt(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm on document</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {checkboxPrompt ? fieldDisplayLabel(checkboxPrompt) : ''}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={promptChecked}
                onChange={(e) => setPromptChecked(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I confirm this acknowledgment
                {checkboxPrompt?.required !== false ? ' *' : ''}
              </Typography>
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCheckboxPrompt(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirmCheckbox}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PdfScrollPreview;
